import { useCallback, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, PenLine, Eraser } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { fmtPrice, fmtPct, fmtCompact, fmtTime, pnlClass, CATEGORY_LABEL } from "@/lib/format";
import { AssetIcon, DataBadge, MarketStatus, DemoBanner, HelpTip } from "@/components/kit";
import { PriceChart } from "@/components/trade/PriceChart";
import { OrderTicket } from "@/components/trade/OrderTicket";
import { PositionsPanel } from "@/components/trade/PositionsPanel";
import { AlertsCard } from "@/components/PriceAlerts";

const TFS = [["1m", "1m", 60], ["5m", "5m", 300], ["15m", "15m", 900], ["30m", "30m", 1800], ["1h", "1H", 3600], ["4h", "4H", 14400], ["1d", "1D", 86400], ["1w", "1S", 604800]];
const INDS = [["ma", "MM20 / MME50", "Médias móveis: suavizam o preço para indicar tendência."], ["bb", "Bollinger", "Bandas que mostram a volatilidade em torno da média."],
  ["rsi", "RSI", "Índice de força relativa: acima de 70 sobrecomprado, abaixo de 30 sobrevendido."], ["macd", "MACD", "Cruzamento de médias que indica mudança de tendência."],
  ["volume", "Volume", "Quantidade negociada em cada período."], ["sr", "Suporte/Resistência", "Mínima e máxima recentes do período visível."]];

const Header = ({ q, fav, onFav }) => (
  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-5 fade-up">
    <div className="flex items-center gap-4">
      <AssetIcon symbol={q.symbol} category={q.category} size={48} />
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="trade-asset-name">{q.name}</h1>
          <DataBadge type={q.data_type} testid="trade-data-badge" />
          <Button size="icon" variant="ghost" onClick={onFav} aria-label="Favoritar" data-testid="trade-fav-btn"><Star className={`h-4 w-4 ${fav ? "fill-primary text-primary" : ""}`} /></Button>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap"><span className="num">{q.symbol}</span>·<span>{CATEGORY_LABEL[q.category]}</span>·<MarketStatus status={q.market_status} />
          {q.source && <span className="text-xs" data-testid="trade-data-source">· Fonte: {q.source}</span>}</div>
      </div>
    </div>
    <div className="flex flex-wrap items-end gap-6">
      <div><div className="num text-3xl font-semibold" data-testid="trade-last-price">{fmtPrice(q.price, q.decimals)}</div>
        <div className={`num text-sm ${pnlClass(q.change_pct)}`}>{q.change > 0 ? "+" : ""}{fmtPrice(q.change, q.decimals)} ({fmtPct(q.change_pct)})</div></div>
      {[["Máx", fmtPrice(q.high, q.decimals)], ["Mín", fmtPrice(q.low, q.decimals)], ["Volume", fmtCompact(q.volume)], ["Atualizado", fmtTime(q.updated_at)]].map(([k, v]) => (
        <div key={k} className="text-sm"><div className="eyebrow">{k}</div><div className="num mt-1">{v}</div></div>))}
    </div>
  </div>
);

export default function Trade() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { theme } = usePrefs();
  const [q, setQ] = useState(null);
  const [tf, setTf] = useState("1h");
  const [chartType, setChartType] = useState("candle");
  const [ind, setInd] = useState({ ma: true, bb: false, rsi: true, macd: false, volume: true, sr: false });
  const [candles, setCandles] = useState([]);
  const [chartErr, setChartErr] = useState("");
  const [pos, setPos] = useState({ open: [], pending: [], closed: [] });
  const [drawMode, setDrawMode] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const tfSec = TFS.find((t) => t[0] === tf)[2];

  usePoll(() => api.get(`/market/quote/${symbol}`).then((r) => {
    const nq = r.data; setQ(nq);
    if (nq.price == null) return;
    setCandles((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      const bucket = Math.floor(Date.now() / 1000 / tfSec) * tfSec;
      if (bucket > last.time) return [...prev, { time: bucket, open: last.close, high: Math.max(last.close, nq.price), low: Math.min(last.close, nq.price), close: nq.price, volume: 0 }];
      return [...prev.slice(0, -1), { ...last, close: nq.price, high: Math.max(last.high, nq.price), low: Math.min(last.low, nq.price) }];
    });
  }).catch(() => navigate("/mercados")), 3000, [symbol, tfSec]);

  usePoll(() => api.get(`/market/candles/${symbol}?tf=${tf}&limit=300`).then((r) => { setCandles(r.data.candles); setChartErr(""); })
    .catch((e) => setChartErr(errMsg(e, "Gráfico indisponível"))), 60000, [symbol, tf]);

  const levels = useMemo(() => {
    const l = [];
    pos.open.forEach((p) => {
      l.push({ price: p.entry_price, color: "#F59E0B", title: `Entrada ${p.side === "buy" ? "C" : "V"} ${p.quantity}` });
      if (p.stop_loss) l.push({ price: p.stop_loss, color: "#EF4444", title: "SL" });
      if (p.take_profit) l.push({ price: p.take_profit, color: "#10B981", title: "TP" });
    });
    pos.pending.forEach((o) => l.push({ price: o.requested_price, color: "#38BDF8", title: `Ordem ${o.order_type}` }));
    return l;
  }, [pos]);

  const markers = useMemo(() => {
    if (!candles.length) return [];
    const first = candles[0].time;
    const snap = (iso) => Math.floor(new Date(iso).getTime() / 1000 / tfSec) * tfSec;
    const m = [];
    [...pos.open, ...pos.closed.slice(0, 20)].forEach((p) => {
      const t = snap(p.opened_at);
      if (t >= first) m.push({ time: t, position: p.side === "buy" ? "belowBar" : "aboveBar", color: p.side === "buy" ? "#10B981" : "#EF4444", shape: p.side === "buy" ? "arrowUp" : "arrowDown", text: "Entrada" });
      if (p.closed_at && snap(p.closed_at) >= first) m.push({ time: snap(p.closed_at), position: "aboveBar", color: "#F59E0B", shape: "circle", text: "Saída" });
    });
    return m.sort((a, b) => a.time - b.time);
  }, [pos, candles, tfSec]);

  const onData = useCallback((d) => setPos(d), []);
  const onDraw = useCallback((price) => { setDrawings((d) => [...d, price]); setDrawMode(false); toast.success("Linha horizontal adicionada"); }, []);

  if (!q) return <div className="text-muted-foreground" data-testid="trade-loading">Carregando ativo…</div>;
  const fav = (user.favorites || []).includes(q.symbol);
  const toggleFav = async () => {
    await (fav ? api.delete(`/me/favorites/${q.symbol}`) : api.post(`/me/favorites/${q.symbol}`));
    setUser({ ...user, favorites: fav ? user.favorites.filter((s) => s !== q.symbol) : [...(user.favorites || []), q.symbol] });
  };

  return (
    <div data-testid="trade-page">
      <Button variant="ghost" className="mb-3 -ml-3" onClick={() => navigate("/mercados")} data-testid="back-to-markets"><ArrowLeft className="mr-2 h-4 w-4" />Mercados</Button>
      <Header q={q} fav={fav} onFav={toggleFav} />
      {q.data_type === "simulated" && <div className="mb-5"><DemoBanner text="Cotação e gráfico SIMULADOS para este ativo. Não representam o preço real de mercado." /></div>}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="surface p-3 md:p-4 xl:col-span-8 flex flex-col" data-testid="chart-card">
          <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-border">
            <div className="flex rounded-lg bg-muted p-1">
              {[["candle", "Velas"], ["line", "Linha"]].map(([k, l]) => (
                <button key={k} onClick={() => setChartType(k)} data-testid={`chart-type-${k}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${chartType === k ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}>{l}</button>))}
            </div>
            <div className="flex flex-wrap rounded-lg bg-muted p-1">
              {TFS.map(([k, l]) => (
                <button key={k} onClick={() => setTf(k)} data-testid={`tf-${k}`}
                  className={`rounded-md px-2.5 py-1.5 num text-xs font-semibold transition-colors ${tf === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>))}
            </div>
            <div className="ml-auto flex gap-1">
              <Toggle size="sm" pressed={drawMode} onPressedChange={setDrawMode} data-testid="draw-hline-toggle" aria-label="Desenhar linha horizontal"><PenLine className="h-4 w-4 mr-1" />Linha</Toggle>
              <Button size="sm" variant="ghost" onClick={() => setDrawings([])} disabled={!drawings.length} data-testid="clear-drawings-btn"><Eraser className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 py-2.5">
            {INDS.map(([k, l, help]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <Toggle size="sm" variant="outline" pressed={ind[k]} onPressedChange={(v) => setInd({ ...ind, [k]: v })} data-testid={`indicator-${k}`} className="text-xs h-8">{l}</Toggle>
                <HelpTip text={help} />
              </span>))}
          </div>
          {drawMode && <p className="text-xs text-primary pb-2" data-testid="draw-hint">Clique no gráfico para posicionar a linha horizontal.</p>}
          <div className="relative min-h-[460px] flex-1" style={{ height: 460 + (ind.rsi ? 110 : 0) + (ind.macd ? 110 : 0) }}>
            {chartErr ? <div className="grid h-full place-items-center text-sm text-muted-foreground" data-testid="chart-error">{chartErr}</div>
              : <PriceChart candles={candles} chartType={chartType} ind={ind} decimals={q.decimals} theme={theme} levels={levels} markers={markers}
                  drawMode={drawMode} drawings={drawings} onDraw={onDraw} fitKey={`${symbol}-${tf}`} />}
          </div>
        </div>
        <div className="xl:col-span-4"><OrderTicket key={q.symbol} q={q} onPlaced={() => setRefreshKey((k) => k + 1)} /><AlertsCard key={`al-${q.symbol}`} q={q} /></div>
        <div className="xl:col-span-12"><PositionsPanel symbol={q.symbol} onData={onData} refreshKey={refreshKey} /></div>
      </div>
    </div>
  );
}
